import type { Category, FileSkipReason, FindingStatus, ReviewStage, ReviewStatus, Severity } from '../types/api';

export const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Informational',
};

export const CATEGORY_LABEL: Record<Category, string> = {
  bug: 'Bug',
  security: 'Security',
  performance: 'Performance',
  'error-handling': 'Error Handling',
  maintainability: 'Maintainability',
  reliability: 'Reliability',
  testing: 'Testing',
};

export const STATUS_LABEL: Record<ReviewStatus, string> = {
  queued: 'Queued',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
};

export const FINDING_STATUS_LABEL: Record<FindingStatus, string> = {
  open: 'Open',
  accepted: 'Accepted',
  dismissed: 'Dismissed',
  resolved: 'Resolved',
};

export const STAGES: Array<{ id: ReviewStage; label: string }> = [
  { id: 'queued', label: 'Queued' },
  { id: 'fetching_files', label: 'Fetching changed files' },
  { id: 'preparing_context', label: 'Filtering & preparing diffs' },
  { id: 'analyzing', label: 'AI analysis' },
  { id: 'validating', label: 'Validating findings' },
  { id: 'saving', label: 'Saving results' },
];

export const SKIP_REASON_LABEL: Record<FileSkipReason, string> = {
  'excluded-pattern': 'Generated, vendored or lock file',
  binary: 'Binary file',
  'no-patch': 'GitHub did not provide a diff',
  'patch-too-large': 'Diff exceeds the per-file size limit',
  'total-budget-exceeded': 'Review size budget exhausted',
  'max-files-exceeded': 'Beyond the maximum files per review',
};

export function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatRelative(iso?: string, now = Date.now()): string {
  if (!iso) return '—';
  const diff = Math.round((now - new Date(iso).getTime()) / 1000);
  if (diff < 45) return 'just now';
  if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  if (diff < 604800) return `${Math.round(diff / 86400)} d ago`;
  return formatDateTime(iso);
}

export const formatNumber = (n?: number) => (n === undefined ? '—' : n.toLocaleString());

export function formatDuration(ms?: number): string {
  if (ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

export const repoLabel = (r: { owner: string; repo: string }) => `${r.owner}/${r.repo}`;
